import Draggable from "react-draggable";
import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { usePersistedPosition } from "../hooks/usePersistedPosition";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";

// Breathing patterns are in seconds: inhale / hold / exhale / rest
const MODES = [
  {
    id: "relax",
    label: "relax",
    subtitle: "4-7-8 calming breath",
    pattern: { inhale: 4, hold: 7, exhale: 8, rest: 0 },
    colors: ["#9aa8ff", "#5b63ee"],
    glow: "rgba(91, 99, 238, 0.45)",
  },
  {
    id: "balance",
    label: "balance",
    subtitle: "box breathing · 4-4-4-4",
    pattern: { inhale: 4, hold: 4, exhale: 4, rest: 4 },
    colors: ["#9fe8b2", "#3ecfb2"],
    glow: "rgba(62, 207, 178, 0.4)",
  },
  {
    id: "restore",
    label: "restore",
    subtitle: "coherent breathing · 5-5",
    pattern: { inhale: 5, hold: 0, exhale: 5, rest: 0 },
    colors: ["#ffce9e", "#f2876b"],
    glow: "rgba(242, 135, 107, 0.4)",
  },
  {
    id: "focus",
    label: "focus",
    subtitle: "extended exhale · 4-2-6",
    pattern: { inhale: 4, hold: 2, exhale: 6, rest: 0 },
    colors: ["#e2ef72", "#7ed163"],
    glow: "rgba(126, 209, 99, 0.4)",
  },
  {
    id: "energize",
    label: "energize",
    subtitle: "quick breaths · 2-1-2",
    pattern: { inhale: 2, hold: 1, exhale: 2, rest: 0 },
    colors: ["#ffa3e0", "#e35cc5"],
    glow: "rgba(227, 92, 197, 0.4)",
  },
  {
    id: "unwind",
    label: "unwind",
    subtitle: "long exhale · 4-6-2",
    pattern: { inhale: 4, hold: 0, exhale: 6, rest: 2 },
    colors: ["#c3b1ff", "#7e5af0"],
    glow: "rgba(126, 90, 240, 0.45)",
  },
];

const formatTime = (secs) =>
  `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;

function BreathingCircle({ breathevisible }) {
  const nodeRef = useRef(null);
  const ballRef = useRef(null);
  const subtitleRef = useRef(null);
  const tlRef = useRef(null);
  const clockTweenRef = useRef(null);
  const inhaleAudioRef = useRef(null);
  const exhaleAudioRef = useRef(null);
  const mutedRef = useRef(false);

  const breatheDrag = usePersistedPosition("breathe", { x: 10, y: 10 });

  const [modeId, setModeId] = useState("relax");
  const [minutes, setMinutes] = useState(2);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("Start");
  const [phaseCount, setPhaseCount] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [muted, setMuted] = useState(false);

  const mode = MODES.find((m) => m.id === modeId);

  useEffect(() => {
    if (!subtitleRef.current) return;
    gsap.fromTo(
      subtitleRef.current,
      { opacity: 0, y: 6 },
      { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }
    );
  }, [modeId, running]);

  useEffect(() => {
    const inhale = new Audio("./audios/breathe-in-3.mp3");
    inhale.playbackRate = 0.85;
    inhale.volume = 0.7;
    const exhale = new Audio("./audios/breathe-out-3.mp3");
    exhale.playbackRate = 0.7;
    exhale.volume = 0.2;
    inhaleAudioRef.current = inhale;
    exhaleAudioRef.current = exhale;
    return () => {
      tlRef.current?.kill();
      clockTweenRef.current?.kill();
      inhale.pause();
      exhale.pause();
    };
  }, []);

  const stopAudio = () => {
    [inhaleAudioRef.current, exhaleAudioRef.current].forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
  };

  const playAudio = (audio) => {
    stopAudio();
    if (audio && !mutedRef.current) {
      audio.play().catch(() => {});
    }
  };

  const stopSession = () => {
    tlRef.current?.kill();
    tlRef.current = null;
    clockTweenRef.current?.kill();
    clockTweenRef.current = null;
    stopAudio();
    gsap.to(ballRef.current, { scale: 1, duration: 0.8, ease: "power2.out" });
    setRunning(false);
    setPhase("Start");
    setPhaseCount(null);
    setTimeLeft(null);
  };

  const startSession = () => {
    const { inhale, hold, exhale, rest } = mode.pattern;
    const cycleLen = inhale + hold + exhale + rest;
    const cycles = Math.max(1, Math.round((minutes * 60) / cycleLen));
    const totalSecs = cycles * cycleLen;

    setRunning(true);
    setTimeLeft(totalSecs);

    const clock = { t: totalSecs };
    clockTweenRef.current = gsap.to(clock, {
      t: 0,
      duration: totalSecs,
      ease: "none",
      onUpdate: () => setTimeLeft(Math.ceil(clock.t)),
    });

    const tl = gsap.timeline({
      repeat: cycles - 1,
      onComplete: stopSession,
    });
    const counter = { v: 0 };

    const addPhase = (name, duration, scale, audio) => {
      if (!duration) return;
      tl.call(() => {
        setPhase(name);
        playAudio(audio);
      });
      tl.to(ballRef.current, { scale, duration, ease: "sine.inOut" }, "<");
      tl.fromTo(
        counter,
        { v: duration },
        {
          v: 0,
          duration,
          ease: "none",
          onUpdate: () => setPhaseCount(Math.max(1, Math.ceil(counter.v))),
        },
        "<"
      );
    };

    addPhase("Inhale", inhale, 1.45, inhaleAudioRef.current);
    addPhase("Hold", hold, 1.45, null);
    addPhase("Exhale", exhale, 1, exhaleAudioRef.current);
    addPhase("Hold", rest, 1, null);

    tlRef.current = tl;
  };

  const handleBallClick = () => {
    if (running) stopSession();
    else startSession();
  };

  const selectMode = (id) => {
    if (id === modeId) return;
    if (running) stopSession();
    setModeId(id);
  };

  const toggleMute = () => {
    setMuted((m) => {
      mutedRef.current = !m;
      if (!m) stopAudio();
      return !m;
    });
  };

  const adjustMinutes = (delta) => {
    setMinutes((m) => Math.min(10, Math.max(1, m + delta)));
  };

  const modeVars = { "--c1": mode.colors[0], "--c2": mode.colors[1], "--glow": mode.glow };

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".header"
      bounds="parent"
      position={breatheDrag.position}
      onStop={breatheDrag.onStop}
    >
      <div
        ref={nodeRef}
        style={{ visibility: breathevisible ? "visible" : "hidden", ...modeVars }}
        className="breathing-widget modern-font absolute"
      >
        <div className="header d-flex justify-content-center align-items-center">
          <div className="grabbar"></div>
        </div>

        <div className="breathe-topbar">
          <div className="breathe-title">Breathe 🍃</div>
          <button className="breathe-sound-btn" onClick={toggleMute} title={muted ? "Unmute" : "Mute"}>
            {muted ? (
              <VolumeOffOutlinedIcon fontSize="small" />
            ) : (
              <VolumeUpOutlinedIcon fontSize="small" />
            )}
          </button>
        </div>

        <div className="breathe-stage">
          <div className="breathe-ring"></div>
          <div
            ref={ballRef}
            className={`glass-ball ${running ? "running" : ""}`}
            onClick={handleBallClick}
          >
            <span className="ball-blob"></span>
            <span className="ball-blob ball-blob-2"></span>
          </div>
        </div>

        <div className="breathe-subtitle" ref={subtitleRef}>
          {running ? (
            <>
              <span className="subtitle-phase">{phase}</span>
              {phaseCount !== null && (
                <span className="subtitle-desc">{phaseCount}</span>
              )}
            </>
          ) : (
            <>
              <span className="subtitle-phase">{mode.label}</span>
              <span className="subtitle-desc">{mode.subtitle}</span>
              <span className="subtitle-hint">tap the orb to begin</span>
            </>
          )}
        </div>

        <div className="breathe-modes">
          {MODES.map((m) => (
            <button
              key={m.id}
              title={`${m.label} · ${m.subtitle}`}
              className={`mode-ball ${m.id === modeId ? "selected" : ""}`}
              style={{ "--c1": m.colors[0], "--c2": m.colors[1], "--glow": m.glow }}
              onClick={() => selectMode(m.id)}
            >
              <span className="ball-blob"></span>
            </button>
          ))}
        </div>

        <div className="breathe-bottom">
          {running ? (
            <div className="breathe-time">{formatTime(timeLeft ?? 0)} left</div>
          ) : (
            <>
              <button
                className="duration-btn"
                onClick={() => adjustMinutes(-1)}
                disabled={minutes <= 1}
              >
                <RemoveRoundedIcon fontSize="inherit" />
              </button>
              <div className="breathe-time">
                {minutes} min{minutes > 1 ? "s" : ""}
              </div>
              <button
                className="duration-btn"
                onClick={() => adjustMinutes(1)}
                disabled={minutes >= 10}
              >
                <AddRoundedIcon fontSize="inherit" />
              </button>
            </>
          )}
        </div>
      </div>
    </Draggable>
  );
}

export default BreathingCircle;
